import { useEffect, useRef } from "react";

type DetailsLike = Pick<
  HTMLDetailsElement,
  "open" | "contains" | "removeAttribute"
>;

/* Native <details> only toggles on its own summary — it knows nothing about
   outside clicks or Escape (NEXT-56). Split from the hook so the logic is
   testable without jsdom. */
export const makeDetailsDismissHandlers = (
  getDetails: () => DetailsLike | null,
) => ({
  onPointerDown: (e: { target: EventTarget | null }) => {
    const details = getDetails();
    if (details?.open && !details.contains(e.target as Node)) {
      details.removeAttribute("open");
    }
  },
  onKeyDown: (e: { key: string }) => {
    if (e.key !== "Escape") return;
    const details = getDetails();
    if (details?.open) details.removeAttribute("open");
  },
});

/* Attach to a <details> dropdown: closes it on pointerdown outside and on
   Escape. Item-select close stays with MenuItem/MenuLink (closeParentDetails). */
export function useDetailsDismiss() {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const { onPointerDown, onKeyDown } = makeDetailsDismissHandlers(
      () => ref.current,
    );
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);
  return ref;
}
